import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Chip,
  Avatar,
  Divider,
  Checkbox,
  Collapse,
  Link,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StripeFundWalletPopup from "./StripeFundWalletPopup";
import { showToast } from "../CoreDataService";

const MIN_AMOUNT = 0.0001;
const MIN_USD_AMOUNT = 1;
const MAX_DIGITS_TOTAL = 15;
const MAX_DECIMAL_PLACES = 6;
const CARD_FUNDING_FEE_RATE = 0.035; // 3.5% card wallet funding fee

const darkColors = {
  background: "#121212",
  paper: "#1e1e1e",
  text: "#ffffff",
  textSecondary: "#b0b0b0",
  primary: "#90caf9",
  border: "#333333",
  surface: "#1e1e1e",
  surfaceAlt: "#2a2a2a",
};

const lightColors = {
  background: "#ffffff",
  paper: "#f8f9fa",
  text: "#212529",
  textSecondary: "#6c757d",
  primary: "#1976d2",
  border: "#dee2e6",
  surface: "#f8f9fa",
  surfaceAlt: "#eef2f7",
};

// Token type options per currency. `label` is what the user sees in the UI.
const TOKEN_OPTIONS = {
  USDT: [
    { value: "erc", label: "ERC" },
    { value: "trc", label: "TRC" },
    { value: "pol", label: "POL" },
    { value: "bep", label: "BSC" }, // BEP shown as BSC in UI
  ],
  HCX: [
    { value: "NATIVE", label: "NATIVE" },
    { value: "erc", label: "ERC" },
  ],
};

// Returns the fixed token type for currencies that don't need user selection.
const getFixedTokenType = (code) => {
  if ((code || "").toUpperCase() === "USDC") return "erc";
  return "NATIVE";
};

/**
 * PabtekPaymentPopup
 *
 * Props:
 *   open         - boolean
 *   onClose      - function
 *   onProceed    - function({ amount, currencyId, currencyCode, usdEquivalent, tokenType })
 *   currencyId   - number | string
 *   currencyCode - string  e.g. "CO2COIN"
 *   userUuid     - string
 *   theme        - "light" | "dark"
 */
const PabtekPaymentPopup = ({
  open,
  onClose,
  onProceed,
  currencyId,
  currencyCode,
  userUuid = "",
  theme = "light",
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const colors = theme === "dark" ? darkColors : lightColors;
  const isDark = theme === "dark";

  // ── Amount input ─────────────────────────────────────────
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");

  // ── Input currency mode (crypto or USD) ──────────────────
  const [inputInUsd, setInputInUsd] = useState(false);

  // ── Market price ─────────────────────────────────────────
  const [unitRate, setUnitRate] = useState(null); // raw marketPrice from API: 1 unit crypto = unitRate USD
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState("");

  // ── Token type ───────────────────────────────────────────
  const tokenOptions = TOKEN_OPTIONS[(currencyCode || "").toUpperCase()] ?? null; // null → no selector
  const defaultTokenType = tokenOptions ? tokenOptions[0].value : getFixedTokenType(currencyCode);
  const [tokenType, setTokenType] = useState(defaultTokenType);

  // ── Payment link ─────────────────────────────────────────
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  // ── Payment method (Pabtek hosted page vs inline Stripe) ───
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // "stripe" | "pabtek"

  // ── Available payment options (from getAchPaymentOption) ───
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [paymentOptionsLoading, setPaymentOptionsLoading] = useState(false);

  // ── Stripe (PayBito Apex) card payment popup ──────────────
  const [showStripePopup, setShowStripePopup] = useState(false);
  const [stripeClientSecret, setStripeClientSecret] = useState("");

  // ── Authorization & disclosure ────────────────────────────
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [disclosureOpen, setDisclosureOpen] = useState(false);

  // ── Derived values ────────────────────────────────────────
  const isUSD = (currencyCode || "").toUpperCase() === "USD";
  const parsedAmount = parseFloat(amount);
  const inputMin = isUSD || inputInUsd ? MIN_USD_AMOUNT : MIN_AMOUNT;
  const isAmountValid = !isNaN(parsedAmount) && parsedAmount >= inputMin;

  // When inputInUsd: user types USD → show crypto equivalent
  // When !inputInUsd: user types crypto → show USD equivalent
  const usdEquivalent =
    !isUSD && !inputInUsd && isAmountValid && unitRate !== null
      ? parsedAmount * unitRate
      : null;
  const cryptoEquivalent =
    !isUSD && inputInUsd && isAmountValid && unitRate !== null && unitRate > 0
      ? parsedAmount / unitRate
      : null;

  // Actual USD amount and crypto amount sent to the API
  const actualUsdAmount = isUSD
    ? parsedAmount
    : inputInUsd
    ? parsedAmount
    : unitRate != null && parsedAmount > 0
    ? parsedAmount * unitRate
    : 0;
  const actualCryptoAmount = isUSD
    ? parsedAmount
    : inputInUsd
    ? unitRate != null && unitRate > 0
      ? parsedAmount / unitRate
      : 0
    : parsedAmount;

  // ── Fee breakdown ─────────────────────────────────────────
  const showBreakdown =
    isAmountValid && !amountError && actualUsdAmount >= MIN_USD_AMOUNT;
  const fundingFeeAmount = showBreakdown
    ? actualUsdAmount * CARD_FUNDING_FEE_RATE
    : 0;
  const totalCardCharge = showBreakdown
    ? actualUsdAmount + fundingFeeAmount
    : 0;

  // What gets credited to the wallet (displayed in the breakdown and disclosure)
  const walletCreditDisplay = isUSD
    ? `$${actualUsdAmount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} USD`
    : actualCryptoAmount > 0
    ? `${actualCryptoAmount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
      })} ${currencyCode}`
    : "—";

  // Active payment options returned by getAchPaymentOption
  const activeOptions = paymentOptions.filter((o) => o?.isActive === 1);
  // Only show the "Pay With" selector when there is a real choice to make
  const showPaymentMethodSelector = activeOptions.length > 1;
  // When exactly one option is active, always use it regardless of selection;
  // otherwise use the user's selection (falls back to default when the API
  // call fails or returns no options).
  const effectivePaymentMethod =
    activeOptions.length === 1
      ? (activeOptions[0].name || "").toLowerCase()
      : paymentMethod;

  const canProceed =
    isAmountValid &&
    actualUsdAmount >= MIN_USD_AMOUNT &&
    !amountError &&
    (isUSD || unitRate !== null) &&
    !priceLoading &&
    !paymentLoading &&
    !paymentOptionsLoading &&
    agreementChecked;

  // ── Fetch market price on open ────────────────────────────
  useEffect(() => {
    if (!open) return;

    // Reset all state
    setAmount("");
    setAmountError("");
    setInputInUsd(false);
    setUnitRate(null);
    setPriceError("");
    setPaymentError("");
    setAgreementChecked(false);
    setDisclosureOpen(false);
    setPaymentMethod("stripe");
    setShowStripePopup(false);
    setStripeClientSecret("");
    setPaymentOptions([]);
    setTokenType(
      TOKEN_OPTIONS[(currencyCode || "").toUpperCase()]
        ? TOKEN_OPTIONS[(currencyCode || "").toUpperCase()][0].value
        : getFixedTokenType(currencyCode)
    );

    // Fetch which ACH payment options (Pabtek / Stripe) are active for this user
    if (userUuid) {
      setPaymentOptionsLoading(true);
      fetch(
        `https://accounts.paybito.com/api/home/getAchPaymentOption?userUuid=${encodeURIComponent(
          userUuid
        )}`,
        {
          method: "GET",
          headers: {
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
        }
      )
        .then((res) => res.json())
        .then((data) => {
          if (data?.error?.error_data === 0 && Array.isArray(data?.paymentOptions)) {
            const options = data.paymentOptions;
            setPaymentOptions(options);
            // Pre-select the gateway flagged isDefault: 1 among the active ones;
            // fall back to Stripe when no gateway is marked default.
            const defaultOption = options.find(
              (o) => o?.isActive === 1 && o?.isDefault === 1
            );
            setPaymentMethod(
              defaultOption ? (defaultOption.name || "").toLowerCase() : "stripe"
            );
          }
        })
        .catch(() => {})
        .finally(() => setPaymentOptionsLoading(false));
    }

    if (!currencyCode) return;

    // USD is entered directly — no conversion needed
    if ((currencyCode || "").toUpperCase() === "USD") {
      setUnitRate(1);
      return;
    }

    setPriceLoading(true);
    fetch("https://accounts.paybito.com/api/home/marketPrice", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        currency: currencyCode,
        baseCurrency: "USD",
        action: 1,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        const mp = data?.marketPrice;
        if (data?.error?.error_data === 0 && mp && mp > 0) {
          // marketPrice = how many USD 1 unit of the currency is worth
          setUnitRate(mp);
        } else {
          setPriceError(data?.error?.error_msg || "Failed to fetch market price.");
        }
      })
      .catch(() => setPriceError("Failed to fetch market price."))
      .finally(() => setPriceLoading(false));
  }, [open, currencyCode]);

  // ── Validation ────────────────────────────────────────────
  const validateAmount = (val) => {
    if (!val) {
      setAmountError("Amount is required.");
      return;
    }

    const dotIndex = val.indexOf(".");
    const intPart = dotIndex >= 0 ? val.slice(0, dotIndex) : val;
    const decPart = dotIndex >= 0 ? val.slice(dotIndex + 1) : "";
    const totalDigits = intPart.length + decPart.length;

    if (decPart.length > MAX_DECIMAL_PLACES) {
      setAmountError(`Amount allows up to ${MAX_DECIMAL_PLACES} decimal places.`);
      return;
    }

    if (totalDigits > MAX_DIGITS_TOTAL) {
      setAmountError(`Amount allows up to ${MAX_DIGITS_TOTAL} digits in total.`);
      return;
    }

    const num = parseFloat(val);
    const effectiveMin = isUSD || inputInUsd ? MIN_USD_AMOUNT : MIN_AMOUNT;
    if (isNaN(num) || num < effectiveMin) {
      const minLabel = isUSD || inputInUsd ? `$${MIN_USD_AMOUNT} USD` : String(effectiveMin);
      setAmountError(`Amount must be at least ${minLabel}.`);
    } else {
      setAmountError("");
    }
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;

    if (val) {
      const dotIndex = val.indexOf(".");
      const intPart = dotIndex >= 0 ? val.slice(0, dotIndex) : val;
      const decPart = dotIndex >= 0 ? val.slice(dotIndex + 1) : "";
      const totalDigits = intPart.length + decPart.length;

      // Block input that already violates the hard limits
      if (decPart.length > MAX_DECIMAL_PLACES || totalDigits > MAX_DIGITS_TOTAL) {
        return;
      }
    }

    setAmount(val);
    validateAmount(val);
  };

  // ── Proceed (Pabtek): generates a hosted Pabtek payment link and opens
  // the externally-hosted pay.pabgw.com page in a new tab ──────────────────
  const handlePabtekProceed = async () => {
    try {
      const res = await fetch(
        "https://accounts.paybito.com/api/transaction/generatePabtekPaymentLink",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({
            userUuid: userUuid || "",
            baseCurrencyId: "1",
            amount: String(actualUsdAmount),          // USD amount charged to card
            counterCurrencyId: String(currencyId ?? ""),
            counterAmount: String(actualCryptoAmount), // crypto amount to receive
            marketPrice: String(isUSD ? "1" : (unitRate ?? "")), // 1 for USD, market rate for others
            tokenType,
          }),
        }
      );

      const data = await res.json();

      if (data?.error?.error_data === 0 && data?.sessionToken) {
        window.open(`https://pay.pabgw.com/s/${data.sessionToken}`, "_blank", "noopener,noreferrer");
        onClose();
        if (typeof onProceed === "function") {
          onProceed({ amount: parsedAmount, currencyId, currencyCode, usdEquivalent, tokenType });
        }
      } else {
        setPaymentError(
          data?.error?.error_msg || "Failed to generate payment link. Please try again."
        );
      }
    } catch {
      setPaymentError("Failed to generate payment link. Please try again.");
    }
  };

  // ── Proceed (Stripe): create the Stripe PaymentIntent then open the
  // inline PayBito Apex (Stripe) card payment popup ─────────────────────────
  const handleStripeProceed = async () => {
    try {
      const res = await fetch(
        "https://accounts.paybito.com/api/transaction/generateStripePaymentIntent",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({
            userUuid: userUuid || "",
            baseCurrencyId: "1",
            counterCurrencyId: String(currencyId ?? ""),
            amount: String(actualUsdAmount),
            counterAmount: String(actualCryptoAmount),
            marketPrice: String(isUSD ? "1" : (unitRate ?? "")),
          }),
        }
      );

      const data = await res.json();
      const clientSecret =
        data?.intentSecret ||
        data?.clientSecret ||
        data?.client_secret ||
        data?.data?.intentSecret ||
        data?.data?.clientSecret ||
        data?.data?.client_secret ||
        "";

      if (data?.error?.error_data === 0 && clientSecret) {
        setStripeClientSecret(clientSecret);
        setShowStripePopup(true);
      } else {
        setPaymentError(
          data?.error?.error_msg || "Failed to initialize card payment. Please try again."
        );
      }
    } catch {
      setPaymentError("Failed to initialize card payment. Please try again.");
    }
  };

  // ── Proceed: dispatches to the selected payment method ───────────────────
  const handleProceed = async () => {
    if (!canProceed) return;
    setPaymentLoading(true);
    setPaymentError("");

    try {
      if (effectivePaymentMethod === "pabtek") {
        await handlePabtekProceed();
      } else {
        await handleStripeProceed();
      }
    } finally {
      setPaymentLoading(false);
    }
  };

  // Called by StripeFundWalletPopup once the Stripe payment succeeds.
  const handleStripeSuccess = (paymentIntent) => {
    setShowStripePopup(false);
    onClose();
    showToast(
      "success",
      `Payment successful! Your ${currencyCode} wallet will be credited shortly.`
    );
    if (typeof onProceed === "function") {
      onProceed({
        amount: parsedAmount,
        currencyId,
        currencyCode,
        usdEquivalent,
        tokenType,
        paymentIntentId: paymentIntent?.id,
      });
    }
  };

  const logoUrl = `https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/${(currencyCode || "").toUpperCase()}.png`;

  // ── Shared toggle button styles ───────────────────────────
  const toggleBtnSx = {
    flex: 1,
    borderRadius: "10px !important",
    border: `1px solid ${colors.border} !important`,
    color: colors.textSecondary,
    fontWeight: 600,
    fontSize: "0.8rem",
    py: 1,
    transition: "all 0.15s ease",
    "&.Mui-selected": {
      backgroundColor: isDark ? "rgba(144,202,249,0.15)" : "rgba(25,118,210,0.1)",
      color: colors.primary,
      borderColor: `${colors.primary} !important`,
      borderWidth: "2px !important",
    },
    "&:hover": {
      backgroundColor: isDark ? "rgba(144,202,249,0.08)" : "rgba(25,118,210,0.06)",
    },
  };

  // ── Disclosure bullet items ───────────────────────────────
  const disclosureItems = [
    "This transaction is a Card Wallet Funding transaction.",
    "The purpose of this transaction is to fund your Wallet.",
    "This transaction does not purchase goods or services from any Merchant.",
    "This transaction does not authorize or complete a Merchant Payment.",
    showBreakdown
      ? `Your Wallet will be credited with ${walletCreditDisplay} upon successful completion of the Card Wallet Funding transaction.`
      : "Your Wallet will be credited upon successful completion of the Card Wallet Funding transaction.",
    "The applicable Card Wallet Funding Fee is included in the Total Card Charge shown above.",
    "Any future payment to a Merchant requires a separate Merchant Payment authorization.",
    "Your electronic authorization, together with the transaction details, agreement versions, date and time, IP address, device information, and session information, will be securely recorded as evidence of this Card Wallet Funding transaction.",
  ];

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "20px",
          backgroundColor: colors.background,
          color: colors.text,
          boxShadow: isDark
            ? "0 24px 64px rgba(0,0,0,0.6)"
            : "0 24px 64px rgba(0,0,0,0.12)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: isMobile ? "100vh" : "92vh",
        },
      }}
    >
      {/* ── Gradient header band ─────────────────────────────── */}
      <Box
        sx={{
          background: isDark
            ? "linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)"
            : "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
          px: 3,
          pt: 3,
          pb: 4,
          position: "relative",
          flexShrink: 0,
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", color: "#fff" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Icon + title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CreditCardIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}
            >
              Fund Your Wallet
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
              Wallet Funding
            </Typography>
          </Box>
        </Box>

        {/* Currency badge */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={logoUrl}
            alt={currencyCode}
            sx={{ width: 40, height: 40, bgcolor: "rgba(255,255,255,0.15)" }}
          >
            {(currencyCode || "?")[0]}
          </Avatar>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>
              {currencyCode || "—"}
            </Typography>
            {isUSD ? (
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                Direct USD payment
              </Typography>
            ) : priceLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CircularProgress size={10} sx={{ color: "rgba(255,255,255,0.6)" }} />
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                  Fetching price…
                </Typography>
              </Box>
            ) : priceError ? (
              <Typography variant="caption" sx={{ color: "#ff8a80" }}>
                Price unavailable
              </Typography>
            ) : unitRate !== null ? (
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                1 {currencyCode} ={" "}
                ${unitRate.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                })}{" "}
                USD
              </Typography>
            ) : null}
          </Box>

          {/* Market rate chip */}
          {!isUSD && unitRate !== null && !priceLoading && (
            <Chip
              icon={
                <TrendingUpIcon
                  sx={{ fontSize: "14px !important", color: "#4caf50 !important" }}
                />
              }
              label="Live Rate"
              size="small"
              sx={{
                ml: "auto",
                backgroundColor: "rgba(76,175,80,0.2)",
                color: "#81c784",
                border: "1px solid rgba(76,175,80,0.3)",
                fontWeight: 600,
                fontSize: "0.65rem",
              }}
            />
          )}
        </Box>
      </Box>

      {/* ── Scrollable content ───────────────────────────────── */}
      <DialogContent sx={{ px: 3, pt: 3, pb: 0, overflowY: "auto", flex: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

          {/* Payment method selector — Pabtek (hosted page) or Stripe (inline card form).
              Only shown when more than one payment option is active; when exactly
              one is active it's used automatically without asking the user. */}
          {showPaymentMethodSelector && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: colors.textSecondary,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  mb: 0.75,
                  display: "block",
                }}
              >
                Pay With
              </Typography>
              <ToggleButtonGroup
                value={paymentMethod}
                exclusive
                onChange={(_, val) => {
                  if (!val) return;
                  setPaymentMethod(val);
                  setPaymentError("");
                }}
                size="small"
                fullWidth
                sx={{ gap: 1 }}
              >
                {activeOptions.map((opt) => (
                  <ToggleButton
                    key={opt.id ?? opt.name}
                    value={(opt.name || "").toLowerCase()}
                    sx={toggleBtnSx}
                  >
                    {opt.name}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Input currency toggle — only for non-USD currencies when rate is available */}
          {!isUSD && unitRate !== null && !priceLoading && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: colors.textSecondary,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  mb: 0.75,
                  display: "block",
                }}
              >
                Enter Amount In
              </Typography>
              <ToggleButtonGroup
                value={inputInUsd ? "USD" : "CRYPTO"}
                exclusive
                onChange={(_, val) => {
                  if (!val) return;
                  setAmount("");
                  setAmountError("");
                  setInputInUsd(val === "USD");
                }}
                size="small"
                fullWidth
                sx={{ gap: 1 }}
              >
                <ToggleButton value="CRYPTO" sx={toggleBtnSx}>
                  {currencyCode}
                </ToggleButton>
                <ToggleButton value="USD" sx={toggleBtnSx}>
                  USD
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Amount input */}
          <Box>
            <TextField
              placeholder="0.0000"
              type="number"
              value={amount}
              onChange={handleAmountChange}
              error={!!amountError}
              helperText={
                amountError ||
                (isUSD || inputInUsd
                  ? `Min: $${MIN_USD_AMOUNT} USD`
                  : `Min: ${MIN_AMOUNT} ${currencyCode || ""} or $${MIN_USD_AMOUNT} USD`)
              }
              fullWidth
              inputProps={{ min: MIN_AMOUNT, step: "any" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        pl: 1.5,
                        borderLeft: `1px solid ${colors.border}`,
                      }}
                    >
                      {inputInUsd ? (
                        <Typography
                          sx={{ color: colors.text, fontWeight: 700, fontSize: "0.9rem" }}
                        >
                          USD
                        </Typography>
                      ) : (
                        <>
                          <Avatar
                            src={logoUrl}
                            alt={currencyCode}
                            sx={{ width: 20, height: 20, bgcolor: colors.primary + "33" }}
                          >
                            {(currencyCode || "?")[0]}
                          </Avatar>
                          <Typography
                            sx={{ color: colors.text, fontWeight: 700, fontSize: "0.9rem" }}
                          >
                            {currencyCode}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: colors.surfaceAlt,
                  color: colors.text,
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  "& fieldset": { borderColor: colors.border },
                  "&:hover fieldset": { borderColor: colors.primary },
                  "&.Mui-focused fieldset": { borderColor: colors.primary, borderWidth: 2 },
                },
                "& .MuiOutlinedInput-input": { py: 1.75 },
                "& .MuiInputLabel-root": { color: colors.textSecondary },
                "& .MuiFormHelperText-root": {
                  color: amountError ? "error.main" : colors.textSecondary,
                  mt: 0.5,
                },
              }}
            />
          </Box>

          {/* Conversion preview — crypto → USD */}
          {usdEquivalent !== null && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.25,
                borderRadius: "10px",
                backgroundColor: isDark
                  ? "rgba(144,202,249,0.08)"
                  : "rgba(25,118,210,0.06)",
                border: `1px solid ${
                  isDark ? "rgba(144,202,249,0.2)" : "rgba(25,118,210,0.15)"
                }`,
              }}
            >
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                You Pay
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: colors.primary, fontWeight: 700, fontSize: "1rem" }}
              >
                ≈ $
                {usdEquivalent.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6,
                })}
              </Typography>
            </Box>
          )}

          {/* Conversion preview — USD → crypto */}
          {cryptoEquivalent !== null && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.25,
                borderRadius: "10px",
                backgroundColor: isDark
                  ? "rgba(144,202,249,0.08)"
                  : "rgba(25,118,210,0.06)",
                border: `1px solid ${
                  isDark ? "rgba(144,202,249,0.2)" : "rgba(25,118,210,0.15)"
                }`,
              }}
            >
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                You Get
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: colors.primary, fontWeight: 700, fontSize: "1rem" }}
              >
                ≈{" "}
                {cryptoEquivalent.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                })}{" "}
                {currencyCode}
              </Typography>
            </Box>
          )}

          {/* Network / Token type selector — only for USDT and HCX */}
          {tokenOptions && (
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: colors.textSecondary,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  mb: 0.75,
                  display: "block",
                }}
              >
                Select Network
              </Typography>
              <ToggleButtonGroup
                value={tokenType}
                exclusive
                onChange={(_, val) => {
                  if (val) setTokenType(val);
                }}
                size="small"
                fullWidth
                sx={{ gap: 1 }}
              >
                {tokenOptions.map((opt) => (
                  <ToggleButton key={opt.value} value={opt.value} sx={toggleBtnSx}>
                    {opt.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          )}

          {/* ── Fee breakdown ─────────────────────────────────── */}
          {showBreakdown && (
            <Box
              sx={{
                borderRadius: "12px",
                border: `1px solid ${colors.border}`,
                overflow: "hidden",
              }}
            >
              {[
                {
                  label: "Funding Amount",
                  value: `$${actualUsdAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} USD`,
                },
                {
                  label: "Card Wallet Funding Fee",
                  value: `≈ $${fundingFeeAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} USD`,
                },
                {
                  label: "Total Card Charge",
                  value: `≈ $${totalCardCharge.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} USD`,
                  highlight: true,
                },
                {
                  label: "Wallet Asset Credited",
                  value: walletCreditDisplay,
                },
                {
                  label: "Wallet",
                  value: `${currencyCode} Wallet`,
                },
              ].map((row, i, arr) => (
                <Box
                  key={row.label}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1.1,
                    backgroundColor: row.highlight
                      ? isDark
                        ? "rgba(144,202,249,0.1)"
                        : "rgba(25,118,210,0.07)"
                      : i % 2 === 0
                      ? colors.surfaceAlt
                      : colors.background,
                    borderBottom:
                      i < arr.length - 1 ? `1px solid ${colors.border}` : "none",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: colors.textSecondary, fontSize: "0.82rem" }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: row.highlight ? colors.primary : colors.text,
                      fontWeight: row.highlight ? 700 : 600,
                      fontSize: "0.82rem",
                    }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* ── Authorization ─────────────────────────────────── */}
          <Box
            sx={{
              borderRadius: "12px",
              border: `1px solid ${colors.border}`,
              p: 2,
              backgroundColor: colors.surfaceAlt,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: colors.text, fontWeight: 700, mb: 1 }}
            >
              Authorization
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.textSecondary, fontSize: "0.82rem", mb: 1.5 }}
            >
              By clicking Fund Wallet, you authorize PayBito to charge your payment
              card and credit the funded Wallet Assets to your PayBito Wallet.
            </Typography>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
              <Checkbox
                checked={agreementChecked}
                onChange={(e) => setAgreementChecked(e.target.checked)}
                size="small"
                sx={{
                  mt: "-3px",
                  p: "4px",
                  color: colors.textSecondary,
                  "&.Mui-checked": { color: colors.primary },
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: colors.textSecondary, fontSize: "0.79rem", lineHeight: 1.55 }}
              >
                I have read and agree to the{" "}
                <Link
                  href="http://paybito.com/wallet-services-agreement"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: colors.primary }}
                >
                  Wallet Services Agreement
                </Link>
                ,{" "}
                <Link href="#" sx={{ color: colors.primary }}>
                  Wallet Funding Terms
                </Link>
                ,{" "}
                <Link
                  href="http://paybito.com/card-wallet-funding-terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: colors.primary }}
                >
                  Card Wallet Funding Terms
                </Link>
                , and{" "}
                <Link
                  href="https://www.paybito.com/wallet-services-fees/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: colors.primary }}
                >
                  Card Wallet Funding Fee Schedule
                </Link>
                .
              </Typography>
            </Box>
          </Box>

          {/* Price unavailable error */}
          {priceError && (
            <Alert
              severity="warning"
              sx={{
                borderRadius: "10px",
                backgroundColor: isDark ? "rgba(255,167,38,0.1)" : undefined,
                color: isDark ? "#ffb74d" : undefined,
                "& .MuiAlert-icon": { color: isDark ? "#ffb74d" : undefined },
              }}
            >
              {priceError}
            </Alert>
          )}

          {/* Payment link error */}
          {paymentError && (
            <Alert
              severity="error"
              sx={{
                borderRadius: "10px",
                backgroundColor: isDark ? "rgba(229,115,115,0.1)" : undefined,
                color: isDark ? "#ef9a9a" : undefined,
                "& .MuiAlert-icon": { color: isDark ? "#ef9a9a" : undefined },
              }}
            >
              {paymentError}
            </Alert>
          )}

          {/* bottom padding inside scroll area */}
          <Box sx={{ pb: 1 }} />
        </Box>
      </DialogContent>

      {/* ── Actions (pinned) ─────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          pt: 2,
          pb: 3,
          flexShrink: 0,
          borderTop: `1px solid ${colors.border}`,
          backgroundColor: colors.background,
        }}
      >
        {/* FUND WALLET button */}
        <Button
          onClick={handleProceed}
          variant="contained"
          disabled={!canProceed}
          fullWidth
          endIcon={
            paymentLoading ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : undefined
          }
          sx={{
            borderRadius: "12px",
            py: 1.5,
            fontWeight: 700,
            fontSize: "0.95rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            background: canProceed
              ? "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)"
              : undefined,
            boxShadow: canProceed ? "0 4px 16px rgba(25,118,210,0.35)" : "none",
            "&:hover": {
              background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)",
              boxShadow: "0 6px 20px rgba(25,118,210,0.45)",
            },
            "&.Mui-disabled": {
              backgroundColor: isDark ? "#2a2a2a" : "#e0e0e0",
              color: isDark ? "#555" : "#9e9e9e",
            },
            mb: 1.5,
          }}
        >
          {paymentLoading ? "Processing…" : "Fund Wallet"}
        </Button>

        {/* Transaction Details & Legal Disclosures toggle */}
        <Button
          onClick={() => setDisclosureOpen((prev) => !prev)}
          fullWidth
          endIcon={
            <ExpandMoreIcon
              sx={{
                transform: disclosureOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
                fontSize: "1.1rem !important",
              }}
            />
          }
          sx={{
            borderRadius: "10px",
            py: 0.875,
            fontWeight: 600,
            fontSize: "0.78rem",
            textTransform: "none",
            color: colors.textSecondary,
            backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
            border: `1px solid ${colors.border}`,
            justifyContent: "space-between",
            "&:hover": {
              backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
              color: colors.text,
            },
          }}
        >
          Transaction Details &amp; Legal Disclosures
        </Button>

        <Collapse in={disclosureOpen}>
          <Box
            sx={{
              mt: 1,
              borderRadius: "10px",
              border: `1px solid ${colors.border}`,
              p: 2,
              backgroundColor: colors.surfaceAlt,
              maxHeight: 200,
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: isDark ? "#444" : "#ccc",
                borderRadius: 3,
              },
            }}
          >
            {disclosureItems.map((text, i) => (
              <Box
                key={i}
                sx={{ display: "flex", gap: 1, mb: i < disclosureItems.length - 1 ? 1 : 0 }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: colors.textSecondary, fontSize: "0.775rem", lineHeight: 1.55 }}
                >
                  • {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    </Dialog>

      {/* ── PayBito Apex (Stripe) card payment popup ─────────────────── */}
      <StripeFundWalletPopup
        open={showStripePopup}
        onClose={() => setShowStripePopup(false)}
        onSuccess={handleStripeSuccess}
        theme={theme}
        currencyCode={currencyCode}
        clientSecret={stripeClientSecret}
        amountUsd={totalCardCharge}
        cryptoAmount={actualCryptoAmount}
      />
    </>
  );
};

export default PabtekPaymentPopup;
